/**
 * How this app's queries and mutations retry, and how a settle finds out that one of them did.
 *
 * Both halves used to be implicit: the policy was a const in `src/index.tsx` (unreachable from a
 * hook without importing the app entry point, and cited by line number in half a dozen comments
 * that rot every time that file moves), and the fact of a retry was not recorded at all. The second
 * half is a correctness input, not a diagnostic: a mutation that retried can have COMMITTED on
 * attempt 1 and then been told "nothing changed" by attempt 2, so any settle that reads a response
 * as PROOF of a no-op has to know whether a retry could be hiding behind it.
 */
import {
  isGlobalSessionExpired,
  isMaintenanceError,
  isSessionExpiredError,
} from '@api-client/api-client';
import { getErrorStatus } from '@utils/errorDetection';

// AIDEV-NOTE: Issue #567 - Auth-aware retry policy. Skip retries when the
// session is expired (recovery flow is handling re-auth), when the error is
// a client-side ERR_SESSION_EXPIRED rejection from the request interceptor,
// and on 401/403/404 (token refresh is already attempted by the axios response
// interceptor, and 403/404 are not transient). Applied to both queries and
// mutations so neither pathway amplifies the cascade.
export const authAwareRetry = (failureCount: number, error: unknown): boolean => {
  if (isGlobalSessionExpired()) return false;
  if (isSessionExpiredError(error)) return false;
  if (isMaintenanceError(error)) return false;
  const err = error as { response?: { status?: number }; status?: number };
  const status = err.response?.status ?? err.status;
  if (status === 401 || status === 403 || status === 404) return false;
  return failureCount < 3;
};

/**
 * `authAwareRetry` for a mutation that treats every 4xx answer as final. For nearly all of them
 * that is simply true: the server read the request and refused it, and the same request gets the
 * same refusal. Without this the refusal is sent four times, with 1 s + 2 s + 4 s of backoff,
 * before its message reaches the person who is waiting on it. Everything else (a 5xx, a failure
 * with no status) keeps the app-wide policy.
 *
 * Two 4xx codes are not refusals of the request itself and are final here all the same, by
 * choice: 408 (the server timed out waiting) and 429 (too many requests). Sending either again
 * unprompted is what the server just asked not to happen, and the person can press the button
 * again.
 *
 * Opt-in per mutation, as its `retry` option. The app-wide default stays `authAwareRetry`:
 * whether a 4xx is final is a property of the endpoint, not of mutations in general.
 *
 * Only for a write that is safe to send twice. A create the server inserts every time it is sent
 * takes `neverRetryCreate` instead.
 */
export const retryUnlessClientError = (failureCount: number, error: unknown): boolean => {
  const status = getErrorStatus(error);
  if (status !== undefined && status >= 400 && status < 500) return false;
  return authAwareRetry(failureCount, error);
};

/**
 * For a create the server inserts every time it receives it, with no idempotency key: the request
 * is sent once and never again on its own, whatever the failure. For example a new post
 * (`POST /questions`), a new partnership (`POST /spaces/{identifier}/partners`) and a new event
 * (`POST /events`); TECH-823 tracks the other creates.
 *
 * A failure with no status (the connection dropped, or axios gave up at its 30 s timeout) does not
 * say whether the server committed: the request may have been received and the row inserted, with
 * only the answer lost. Sending it again can then insert the same row twice. A 5xx does not settle
 * it either: a handler can fail after its commit (`create_question` says so of its own post-commit
 * work, D3 of backend #719), and a gateway can answer 502 or 504 while the handler goes on to
 * commit. So the person sends again, with a tap, once they have checked. A create that uses this
 * keeps what they typed, says it may have saved (`mayHaveCommitted`, below; a refusal says what
 * failed) and gives them the button back.
 *
 * One retry it turns down can come much later than the rest: under the default `networkMode`
 * ('online'), an attempt that is to be retried first waits, paused, while the app is offline or
 * out of focus, and is sent again once it is back, possibly long after the first attempt
 * committed. Returning false fails the mutation before that pause.
 *
 * Two re-sends remain, and neither can add a row:
 * - A mutation started while TanStack believes the app is offline is paused BEFORE `mutationFn`
 *   runs. Nothing was sent, so the resume sends it for the first time.
 * - The axios response interceptor's refresh-and-replay of a 401 (`api-client.ts`): the server
 *   refused the request in its auth dependency, before the handler's body ran, and the replay
 *   happens once, inside the same `mutationFn` call.
 *
 * This is the client half. The real fix is an idempotency key the backend honours (backend #1146),
 * as the Partners stats batches already do (`src/lib/partners/partnerStatsQueue.ts`, backend
 * `record_space_partner_stats`): with one, a lost answer is safe to send again, and a create could
 * take `retryUnlessClientError` like the other writes.
 *
 * Takes the same arguments as the other two policies, so any of the three can be a mutation's
 * `retry` option.
 */
export const neverRetryCreate = (_failureCount: number, _error: unknown): boolean => false;

/**
 * True when a failed create may have saved all the same, for the reasons `neverRetryCreate` gives:
 * the failure has no status (the request may have been received and only the answer lost), or it
 * is a 5xx (the handler can fail after its commit, or a gateway answer while it commits). A create
 * that fails this way says it is not sure it went through, so the person checks before sending it
 * again, and reads their list again so a row that did save shows up there. A 4xx is a refusal:
 * nothing was written, and the create keeps its own copy for it.
 *
 * Status first, so a failure the server answered never needs more than its status. A 503 cannot
 * have committed for these creates today, since none of their handlers raises one (their 503s come
 * from the auth dependency, a JWKS fetch or a reactivation, or from nginx, before the handler
 * runs), and it reads as "may have saved" all the same, on purpose: what sits in front of a
 * handler changes.
 *
 * With no status, two failures are told apart because they are known never to have been sent: the
 * request interceptor rejects with them before anything leaves the device, a session-expired error
 * while the session is expired and a MaintenanceError during maintenance (`api-client.ts`). Every
 * other failure with no status reads as "may have saved", by choice, a throw of the create's own
 * code included: the screens' try blocks also wrap local work that runs before the send (a payload
 * builder) and after a success (a TypeError reading the answer). A throw after a success did save,
 * one before the send looks the same as a lost answer here, and asking the person to check first
 * costs less than a duplicate.
 */
export const mayHaveCommitted = (error: unknown): boolean => {
  const status = getErrorStatus(error);
  if (status !== undefined) return status >= 500;
  return !isSessionExpiredError(error) && !isMaintenanceError(error);
};

/**
 * Attempts made per mutation INVOCATION, keyed by that invocation's variables object.
 *
 * The key is the one value query-core guarantees is (a) the same reference on every attempt of one
 * invocation and (b) handed to `onSettled` alongside the result: `Mutation#execute(variables)`
 * closes over it and passes it both to `options.mutationFn(variables)` and to
 * `options.onSettled(data, error, variables, context)` (query-core 5.101.4 `mutation.js`). Nothing
 * else spans both worlds - `mutationFn` and `retry` never see the `onMutate` context, and `retry`
 * does not even see the variables - so a counter held anywhere else (a `useRef`, a module scalar)
 * is per-HOOK-INSTANCE, and two concurrent `.mutate()` calls from one hook would read each other's
 * attempts. A rapid Going double-tap is exactly that shape.
 *
 * Weak by construction: the entry dies with the variables object, so there is nothing to clean up
 * and no settle-time delete that a still-running sibling invocation could reset out from under.
 *
 * Requires callers to pass a FRESH variables object per invocation, which every call site does
 * (they all build an object literal at the point of the tap). If one ever reused a single object
 * across two concurrent invocations the counts would merge - and merging can only ever OVER-count,
 * because the ledger is append-only, so the worst case is a settle believing it retried when it did
 * not. That degrades to the wider sweep, never to the dangerous "provably nothing changed".
 */
const attemptsByVariables = new WeakMap<object, number>();

/**
 * Records one attempt. Call it FIRST in `mutationFn`, which query-core invokes exactly once per
 * attempt - so this counts requests that actually left the client, not retry DECISIONS. That is the
 * granularity the settle wants: the axios response interceptor's own 401-refresh retry re-issues a
 * request the server rejected before touching a row, and it happens inside one `mutationFn` call,
 * so it correctly does not register here.
 *
 * A non-object `variables` is a silent no-op rather than the `WeakMap#set` `TypeError` it would
 * otherwise throw: this runs first inside `mutationFn`, so an unguarded throw here would fail the
 * mutation before the request ever went out. Both real call sites are typed to always pass an
 * object, so this is a defensive backstop, not a path either is expected to take.
 */
export const recordMutationAttempt = (variables: unknown): void => {
  if (typeof variables !== 'object' || variables === null) return;
  attemptsByVariables.set(variables, (attemptsByVariables.get(variables) ?? 0) + 1);
};

/**
 * True when this invocation sent more than one request, so an earlier attempt may have committed
 * server-side and had its response lost. Call it from `onSettled` with that callback's own
 * `variables`. False for an invocation that never called `recordMutationAttempt` (nothing was
 * sent, e.g. an offline-paused mutation), which is correct: no request, no hidden commit.
 */
export const mutationWasRetried = (variables: object): boolean =>
  (attemptsByVariables.get(variables) ?? 0) > 1;
